/** Lossless browser transport for direct study authoring operations; no UI state or legacy fallback. */
import {
  SHOPIFY_STUDY_AUTHORING_CREATE_OPERATION,
  SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION,
  SHOPIFY_STUDY_AUTHORING_INSIGHT_SEED_OPERATION,
  SHOPIFY_STUDY_AUTHORING_DELETE_OPERATION,
  SHOPIFY_STUDY_AUTHORING_DELETION_STATUS_OPERATION,
  SHOPIFY_STUDY_AUTHORING_LIST_OPERATION,
  SHOPIFY_STUDY_AUTHORING_PREPARE_OPERATION,
  SHOPIFY_STUDY_AUTHORING_READ_OPERATION,
  SHOPIFY_STUDY_AUTHORING_RESULTS_OPERATION,
  SHOPIFY_STUDY_RESPONSE_SEARCH_OPERATION,
  SHOPIFY_STUDY_AUTHORING_RUN_OPERATION,
  SHOPIFY_STUDY_AUTHORING_SAVE_OPERATION,
  SHOPIFY_STUDY_AUTHORING_STATUS_OPERATION,
  studyAuthoringCreateSchema,
  studyAuthoringDeletionStatusSchema,
  studyAuthoringDraftSchema,
  studyAuthoringListResponseSchema,
  studyAuthoringListSchema,
  studyAuthoringReadSchema,
  studyAuthoringResultsSchema,
  studyAuthoringRunSchema,
  studyAuthoringSaveSchema,
  studyAuthoringStatusSchema,
  type ShopifyOperationDefinition,
  type StudyAuthoringRecord,
} from '@testfeed/shopify-contracts'
import { throwMerchantFacingApiError } from '../../shopifyApiError'
import { shopifyApiFetch, type ShopifyAppBridge } from '../../shopifyApiFetch'
import type { z } from 'zod'

export type { StudyAuthoringRecord }
export type StudyAuthoringDraft = z.infer<typeof studyAuthoringDraftSchema>
export type StudyAuthoringCreateInput = z.infer<typeof studyAuthoringCreateSchema>
export type StudyAuthoringSaveInput = z.infer<typeof studyAuthoringSaveSchema>
export type StudyAuthoringReadInput = z.infer<typeof studyAuthoringReadSchema>
export type StudyAuthoringListInput = z.infer<typeof studyAuthoringListSchema>
export type StudyAuthoringListResponse = z.infer<typeof studyAuthoringListResponseSchema>
export type StudyAuthoringStatusResponse = z.infer<typeof studyAuthoringStatusSchema>
export type StudyAuthoringPrepareInput = StudyAuthoringSaveInput
export type StudyAuthoringRunInput = z.infer<typeof studyAuthoringRunSchema>
export type StudyAuthoringRunResponse = z.infer<(typeof SHOPIFY_STUDY_AUTHORING_RUN_OPERATION)['responseSchema']>
export type StudyAuthoringResultsInput = z.infer<typeof studyAuthoringResultsSchema>
export type StudyAuthoringResultsResponse = z.infer<
  (typeof SHOPIFY_STUDY_AUTHORING_RESULTS_OPERATION)['responseSchema']
>
export type StudyAuthoringDeleteInput = z.infer<(typeof SHOPIFY_STUDY_AUTHORING_DELETE_OPERATION)['requestSchema']>
export type StudyAuthoringDeleteResponse = z.infer<(typeof SHOPIFY_STUDY_AUTHORING_DELETE_OPERATION)['responseSchema']>
export type StudyAuthoringDeletionStatusInput = z.infer<typeof studyAuthoringDeletionStatusSchema>
export type StudyAuthoringDeletionStatusResponse = z.infer<
  (typeof SHOPIFY_STUDY_AUTHORING_DELETION_STATUS_OPERATION)['responseSchema']
>

type StudyAuthoringOperation<Input, Output> = ShopifyOperationDefinition<Input, Output>

// A browser-side integrity failure carries no merchant wording of its own: the code names the state (`pageErrorFrom`)
// and the Worker's load-error words (load page) or `readiness.onReadFailure` (request-failure banner) supply the text.
function invalidAuthoringResponse(): Error {
  return new Error('STUDY_AUTHORING_INVALID: authoring response failed validation')
}

function sourceMismatch(): Error {
  return new Error('STUDY_BUILDER_SOURCE_MISMATCH: response identity did not match the request')
}

function assertMatchingStudyId(value: unknown, expectedStudyId: string): void {
  if (typeof value !== 'object' || value === null || !('studyId' in value)) return
  const responseStudyId = (value as { studyId?: unknown }).studyId
  if (responseStudyId !== undefined && responseStudyId !== expectedStudyId) throw sourceMismatch()
}

async function requestStudyAuthoringOperation<Input, Output>(
  shopify: ShopifyAppBridge,
  operation: StudyAuthoringOperation<Input, Output>,
  input: Input,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<Output> {
  const requestValidation = operation.requestSchema.safeParse(input)
  if (!requestValidation.success) throw invalidAuthoringResponse()

  let url = operation.path
  const init: RequestInit = { method: operation.method }
  if (operation.method === 'GET') {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(input as unknown as Record<string, unknown>)) {
      if (value !== undefined) query.set(key, String(value))
    }
    const queryString = query.toString()
    if (queryString) url = `${url}?${queryString}`
  } else {
    init.headers = { 'Content-Type': 'application/json' }
    init.body = JSON.stringify(input)
  }
  if (signal) init.signal = signal

  const response = await shopifyApiFetch(shopify, url, { init, fetchImpl })
  // The Worker fills the merchant wording on every `/api` error; the builder renders it and passes no
  // copy of its own. A response with no server words fails loud (ShopifyApiRequestError), and the page
  // shows the server words it already holds for the state.
  if (!response.ok) return throwMerchantFacingApiError(response, operation.id)

  const payload = await response.json().then(
    (body: unknown) => body,
    () => {
      throw invalidAuthoringResponse()
    },
  )
  const responseValidation = operation.responseSchema.safeParse(payload)
  if (!responseValidation.success) throw invalidAuthoringResponse()
  return responseValidation.data
}

function assertStatusRevision(status: StudyAuthoringStatusResponse, expectedStudyId: string): void {
  if (status.authoring.studyId !== expectedStudyId) throw sourceMismatch()
  if (status.state.revision !== status.authoring.revision) throw invalidAuthoringResponse()
}

export async function createStudyAuthoring(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringCreateInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringRecord> {
  return requestStudyAuthoringOperation(shopify, SHOPIFY_STUDY_AUTHORING_CREATE_OPERATION, input, signal, fetchImpl)
}

export type InsightStudyConfirmation = Readonly<
  z.infer<(typeof SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION)['requestSchema']> &
    z.infer<(typeof SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION)['responseSchema']>
>

/** Resolve once for a new deliberate confirmation, never as part of retrying its submission. */
export async function resolveInsightStudyConfirmation(
  shopify: ShopifyAppBridge,
  input: z.infer<(typeof SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION)['requestSchema']>,
  signal?: AbortSignal,
): Promise<InsightStudyConfirmation> {
  const preview = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION,
    input,
    signal,
  )
  if (preview.saved) assertInsightStudySource(preview.saved, input)
  return Object.freeze({ batchId: input.batchId, productId: input.productId, ...preview })
}

function assertInsightStudySource(saved: StudyAuthoringRecord, input: { batchId: string; productId: string }): void {
  if (saved.draft.selectedProductId !== input.productId || saved.draft.insightSetupSeed?.batchId !== input.batchId)
    throw sourceMismatch()
}

/** Submit only the identity the merchant confirmed. An uncertain response must not resolve a successor. */
export async function seedInsightStudyAuthoring(
  shopify: ShopifyAppBridge,
  confirmation: InsightStudyConfirmation,
  signal?: AbortSignal,
): Promise<StudyAuthoringRecord> {
  const { batchId, productId, creationIntentId } = confirmation
  const saved =
    confirmation.saved ??
    (await requestStudyAuthoringOperation(
      shopify,
      SHOPIFY_STUDY_AUTHORING_INSIGHT_SEED_OPERATION,
      { batchId, productId, creationIntentId, confirmed: true },
      signal,
    ))
  assertInsightStudySource(saved, confirmation)
  return saved
}

export async function saveStudyAuthoring(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringSaveInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringRecord> {
  const saved = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_SAVE_OPERATION,
    input,
    signal,
    fetchImpl,
  )
  assertMatchingStudyId(saved, input.studyId)
  return saved
}

export async function readStudyAuthoring(
  shopify: ShopifyAppBridge,
  studyId: string,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringRecord> {
  const record = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_READ_OPERATION,
    { studyId },
    signal,
    fetchImpl,
  )
  assertMatchingStudyId(record, studyId)
  return record
}

export async function listStudyAuthoring(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringListInput = {},
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringListResponse> {
  return requestStudyAuthoringOperation(shopify, SHOPIFY_STUDY_AUTHORING_LIST_OPERATION, input, signal, fetchImpl)
}

export async function prepareStudyAuthoring(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringPrepareInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringStatusResponse> {
  const status = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_PREPARE_OPERATION,
    input,
    signal,
    fetchImpl,
  )
  assertStatusRevision(status, input.studyId)
  return status
}

export async function readStudyAuthoringStatus(
  shopify: ShopifyAppBridge,
  studyId: string,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringStatusResponse> {
  const status = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_STATUS_OPERATION,
    { studyId },
    signal,
    fetchImpl,
  )
  assertStatusRevision(status, studyId)
  return status
}

export async function runStudyAuthoring(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringRunInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringRunResponse> {
  const result = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_RUN_OPERATION,
    input,
    signal,
    fetchImpl,
  )
  assertMatchingStudyId(result, input.studyId)
  return result
}

export async function readStudyAuthoringResults(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringResultsInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringResultsResponse> {
  const results = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_RESULTS_OPERATION,
    input,
    signal,
    fetchImpl,
  )
  if (results.study) assertMatchingStudyId(results.study, input.studyId)
  return results
}

export async function deleteStudyAuthoring(
  shopify: ShopifyAppBridge,
  studyId: string,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringDeleteResponse> {
  const deleted = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_DELETE_OPERATION,
    { studyId, confirmed: true },
    signal,
    fetchImpl,
  )
  assertMatchingStudyId(deleted, studyId)
  return deleted
}

export async function readStudyAuthoringDeletionStatus(
  shopify: ShopifyAppBridge,
  input: StudyAuthoringDeletionStatusInput,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<StudyAuthoringDeletionStatusResponse> {
  const status = await requestStudyAuthoringOperation(
    shopify,
    SHOPIFY_STUDY_AUTHORING_DELETION_STATUS_OPERATION,
    input,
    signal,
    fetchImpl,
  )
  assertMatchingStudyId(status, input.studyId)
  if (input.operationId && status.operationId !== input.operationId) throw sourceMismatch()
  return status
}

export function searchStudyResponses(
  shopify: ShopifyAppBridge,
  input: z.infer<(typeof SHOPIFY_STUDY_RESPONSE_SEARCH_OPERATION)['requestSchema']>,
  signal?: AbortSignal,
  fetchImpl?: typeof fetch,
) {
  return requestStudyAuthoringOperation(shopify, SHOPIFY_STUDY_RESPONSE_SEARCH_OPERATION, input, signal, fetchImpl)
}
